import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

interface ChatMsg { role: 'user' | 'bot'; text: string }

/**
 * 智能客服浮窗（公开端右下角）
 * 调 /api/chat：已接入专用大模型 → LLM 回答；未接入 → 规则 FAQ；
 * 无法解决 → 引导转真人客服（微信二维码）
 */
export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState('');
  const [csText, setCsText] = useState('扫码添加客服微信，获取兑换码 / 咨询开通');
  const [stats, setStats] = useState('');
  const [unread, setUnread] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(api('/api/support/config')).then(r => r.json()).then(d => {
      setQr(d.qr || '');
      setCsText(d.text || '');
      setStats(d.stats || '');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setUnread(false);
    setMsgs(m => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const r = await fetch(api('/api/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      const d = await r.json();
      if (d.qr) setQr(d.qr);
      if (d.csText) setCsText(d.csText);
      setMsgs(m => [...m, { role: 'bot', text: d.reply || '暂时无法回答，请稍后再试或联系客服。' }]);
    } catch {
      setMsgs(m => [...m, { role: 'bot', text: '网络异常，请稍后再试，或直接扫码联系真人客服。' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* 悬浮按钮 */}
      <button
        onClick={() => { setOpen(!open); setUnread(false); }}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-[0_0_24px_rgba(34,211,238,0.45)] hover:shadow-[0_0_32px_rgba(34,211,238,0.7)] transition-all"
        aria-label="智能客服"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        )}
        {!open && unread && <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-red-500 border-2 border-[#0a0a0f]" />}
      </button>

      {/* 聊天面板 */}
      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[min(360px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-cyan-500/30 bg-[#0d1220]/95 shadow-[0_0_40px_rgba(34,211,238,0.25)] backdrop-blur">
          {/* 头部 */}
          <div className="flex items-center gap-3 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 0 1 7 7c0 2-.5 3.5-1.5 5-.8 1.2-1 2.5-.7 4.3l.2 1.2H7l.2-1.2c.3-1.8.1-3.1-.7-4.3C5.5 12.5 5 11 5 9a7 7 0 0 1 7-7z"/><circle cx="9" cy="9" r=".6"/><circle cx="12" cy="9" r=".6"/><circle cx="15" cy="9" r=".6"/></svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-cyan-200">智能客服</div>
              <div className="text-[11px] text-gray-500">免费模型 · 兑换码 · 使用帮助</div>
            </div>
          </div>

          {/* 消息区 */}
          <div ref={bodyRef} className="max-h-[320px] space-y-3 overflow-y-auto px-4 py-3">
            <div className="text-[11px] text-gray-500">{stats}</div>
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-xl rounded-tl-sm bg-gray-800/80 px-3 py-2 text-xs text-gray-200">
                你好，我是智能客服 👋 可以问我：免费模型怎么找、兑换码怎么激活、API Key 如何注册、免费额度、支付与订阅等问题。
              </div>
            </div>
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-xs ${m.role === 'user' ? 'rounded-tr-sm bg-gradient-to-br from-cyan-500 to-blue-600 text-white' : 'rounded-tl-sm bg-gray-800/80 text-gray-200'}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="rounded-xl rounded-tl-sm bg-gray-800/80 px-3 py-2 text-xs text-gray-400">思考中…</div></div>}
          </div>

          {/* 转人工 */}
          <div className="border-t border-gray-700/50 px-4 py-2.5 bg-gray-900/60">
            {qr ? (
              <div className="flex items-center gap-3">
                <img src={qr} alt="客服微信" className="h-14 w-14 rounded-lg border border-gray-700 object-contain bg-white" />
                <div className="text-[11px] text-gray-400 leading-relaxed">{csText}<br/><span className="text-gray-500">问题没解决？扫码联系真人客服</span></div>
              </div>
            ) : (
              <div className="text-[11px] text-gray-500">问题没解决？{csText}（管理员可在后台「内容 → 客服微信」配置二维码）</div>
            )}
          </div>

          {/* 输入区 */}
          <div className="flex items-center gap-2 border-t border-gray-700/50 px-3 py-2.5">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="输入你的问题…"
              className="flex-1 rounded-lg border border-gray-700 bg-gray-800/60 px-3 py-2 text-xs text-gray-200 placeholder:text-gray-500 focus:border-cyan-500/60 focus:outline-none"
            />
            <button onClick={send} disabled={busy || !input.trim()} className="rounded-lg bg-cyan-500/20 px-3 py-2 text-xs text-cyan-300 hover:bg-cyan-500/30 disabled:opacity-40">
              {busy ? '…' : '发送'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
